import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { devicesService } from '../../services/devices';
import type { Device } from '../../types/device';
import BoardBluetoothScanner from './BoardBluetoothScanner';

/**
 * How long a name the device may advertise itself under. The BLE scan response
 * it travels in has room for this many characters and no more; past it the
 * firmware truncates, and a cut name is exactly what an app may fail to
 * recognise. Kept in step with Device::BLUETOOTH_PROXY_NAME_MAX.
 */
const NAME_MAX = 29;

/** Six hex octets, the form the server stores and compares against. */
const MAC_FORMAT = /^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/;

interface DeviceBluetoothProxyCardProps {
  gymSlug: string;
  device: Device;
  onDeviceChange: (device: Device) => void;
}

/**
 * The switch that puts this box on air in front of a board, pretending to be it.
 *
 * With it on, the device advertises itself beside the wall and the climbers'
 * own Moonboard or Kilter app connects to it instead of to the board: the frames
 * are passed through untouched — the LEDs behave exactly as before — and we get
 * to see which problem was lit. Off by default, because the box then shows up in
 * every phone's Bluetooth list in the room, which is not something to do to a
 * gym that did not ask for it.
 *
 * What it does *not* touch is the rest: lighting problems from DivePeaks and
 * scanning for nearby boards both work the same either way. The radio simply
 * comes up when the first of the three needs it, and with the proxy off that is
 * the first command that uses it rather than boot.
 *
 * Which wall the box stands in front of is a MAC, picked off the air rather than
 * typed: the scan below is the same one the board form uses. It may well be a
 * wall nobody has added to DivePeaks — the air does not know about our
 * catalogue — and the proxy runs anyway, so the mismatch is a warning here and
 * not a refusal. What is lost is only the reading of what gets lit, since the
 * protocol to decode a frame with comes from the board's type.
 */
export default function DeviceBluetoothProxyCard({
  gymSlug,
  device,
  onDeviceChange,
}: DeviceBluetoothProxyCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const proxy = device.bluetooth_proxy;
  const [enabled, setEnabled] = useState(proxy.enabled);
  const [deviceName, setDeviceName] = useState(proxy.device_name ?? '');
  const [boardName, setBoardName] = useState(proxy.board_name ?? '');
  const [boardAddress, setBoardAddress] = useState(proxy.board_address ?? '');
  const [saving, setSaving] = useState(false);

  const trimmedName = deviceName.trim();
  const trimmedAddress = boardAddress.trim();
  const nameTooLong = trimmedName.length > NAME_MAX;
  const addressMalformed = trimmedAddress !== '' && !MAC_FORMAT.test(trimmedAddress);
  // On, the firmware needs both to go on air at all; off, there is nothing to
  // be missing and half-filled fields are worth keeping for next time.
  const incomplete = enabled && (trimmedName === '' || trimmedAddress === '');
  const canSave = !saving && !nameTooLong && !addressMalformed && !incomplete;

  // A MAC that is none of this device's boards. Silent otherwise: the telemetry
  // simply never arrives, and it is weeks before anybody wonders why.
  const unknownBoard =
    enabled &&
    !addressMalformed &&
    trimmedAddress !== '' &&
    !device.boards.some(
      (board) => (board.bluetooth_address ?? '').toUpperCase() === trimmedAddress.toUpperCase()
    );

  const save = async () => {
    if (!canSave) return;

    setSaving(true);
    try {
      const updated = await devicesService.update(gymSlug, device.id, {
        bluetooth_proxy_enabled: enabled,
        bluetooth_proxy_device_name: trimmedName || null,
        bluetooth_proxy_board_name: boardName.trim() || null,
        bluetooth_proxy_board_address: trimmedAddress || null,
      });
      onDeviceChange(updated);
      setDeviceName(updated.bluetooth_proxy.device_name ?? '');
      setBoardName(updated.bluetooth_proxy.board_name ?? '');
      setBoardAddress(updated.bluetooth_proxy.board_address ?? '');
      // The device applies this at its next provisioning call, without waiting
      // for anything — but the Bluetooth stack it may have brought up cannot be
      // switched off again (NimBLE resets the board on deinit), so the RAM stays
      // taken until a restart. Hence "restart to apply it fully", never "it will
      // take effect on restart", which would be untrue of the change itself.
      toast({
        title: t('gymAdmin.devices.proxy.saved'),
        description: t('gymAdmin.devices.proxy.restartHint'),
      });
    } catch (error) {
      toast({
        title: t('gymAdmin.devices.proxy.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="proxy-enabled" className="text-base">
              {t('gymAdmin.devices.proxy.title')}
            </Label>
            <p className="text-sm text-muted-foreground">{t('gymAdmin.devices.proxy.hint')}</p>
          </div>
          <Switch
            id="proxy-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
            aria-label={t('gymAdmin.devices.proxy.title')}
          />
        </div>

        {enabled && (
          <div className="space-y-4 border-t pt-4">
            <div className="space-y-2">
              <Label htmlFor="proxy-device-name">
                {t('gymAdmin.devices.proxy.fields.deviceName')}
              </Label>
              <Input
                id="proxy-device-name"
                value={deviceName}
                onChange={(event) => setDeviceName(event.target.value)}
                aria-invalid={nameTooLong || undefined}
              />
              <div className="flex justify-between gap-2 text-xs">
                <span className="text-muted-foreground">
                  {t('gymAdmin.devices.proxy.fields.deviceNameHint')}
                </span>
                {/* The count is the point: the ceiling is the radio's, not a
                    house style, and finding out in the gym is expensive. */}
                <span className={nameTooLong ? 'font-medium text-destructive' : 'text-muted-foreground'}>
                  {trimmedName.length}/{NAME_MAX}
                </span>
              </div>
            </div>

            <div className="space-y-4 rounded-md border p-3">
              <BoardBluetoothScanner
                gymSlug={gymSlug}
                deviceId={device.id}
                selectedAddress={boardAddress}
                onSelect={(result) => {
                  setBoardAddress(result.address);
                  setBoardName(result.name ?? boardName);
                }}
              />

              <div className="space-y-2">
                <Label htmlFor="proxy-board-name">
                  {t('gymAdmin.devices.proxy.fields.boardName')}
                </Label>
                <Input
                  id="proxy-board-name"
                  value={boardName}
                  onChange={(event) => setBoardName(event.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="proxy-board-address">
                  {t('gymAdmin.devices.proxy.fields.boardAddress')}
                </Label>
                <Input
                  id="proxy-board-address"
                  value={boardAddress}
                  onChange={(event) => setBoardAddress(event.target.value)}
                  placeholder="94:B5:55:5F:44:CE"
                  className="font-mono"
                  aria-invalid={addressMalformed || undefined}
                />
                {addressMalformed && (
                  <p className="text-xs text-destructive">
                    {t('gymAdmin.devices.proxy.fields.boardAddressInvalid')}
                  </p>
                )}
              </div>
            </div>

            {unknownBoard && (
              <p
                data-testid="proxy-unknown-board"
                className="flex items-start gap-2 rounded-md bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                {t('gymAdmin.devices.proxy.unknownBoard')}
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end">
          {/* Named for what it saves: the device's own form has a Save of its
              own right above, and two of them would be a coin toss. */}
          <Button type="button" onClick={save} disabled={!canSave}>
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t('gymAdmin.devices.proxy.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
